import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../services/api";
import { BackButton } from "../../../components/button/back-button";
import { useToast } from "../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../utils";
import { ProtocolAccess } from "./access";
import { PartySelect } from "./party-select";
import { priorityLabels, type ProtocolPriority } from "../../../types/protocol";
import { LoadingButton } from "../../../components/button/loading-button";
import { ProtocolAttachmentsSection } from "./attachments-section";
export default function CreateProtocolPage() {
  return (
    <ProtocolAccess>
      <CreateProtocol />
    </ProtocolAccess>
  );
}
function CreateProtocol() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { error, success } = useToast();
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(""),
    [description, setDescription] = useState("");
  const [requesterId, setRequesterId] = useState(""),
    [assignedToSystemUserId, setAssigned] = useState("");
  const [priority, setPriority] = useState<ProtocolPriority>("NORMAL");
  const [files, setFiles] = useState<File[]>([]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    let createdId: string | undefined;
    try {
      const res = await api.post("/protocols", {
        title: title.trim(),
        description: description.trim(),
        priority,
        ...(requesterId ? { requesterId } : {}),
        ...(assignedToSystemUserId ? { assignedToSystemUserId } : {}),
        ...(params.get("conversationId")
          ? { conversationId: params.get("conversationId") }
          : {}),
      });
      createdId = res.data.id;
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        await api.post(`/protocols/${res.data.id}/attachments`, formData);
      }
      success("Protocolo registrado.");
      navigate(`/painel/protocolos/${res.data.id}`);
    } catch (err) {
      if (createdId) {
        error(
          `Protocolo registrado, mas não foi possível enviar os anexos: ${getApiErrorMessage(err)}`,
        );
        navigate(`/painel/protocolos/${createdId}`);
      } else {
        error(getApiErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title mb-6">Novo protocolo</h1>
      <form className="space-y-6" onSubmit={submit}>
        <label className="form-label block">
          Título
          <input
            className="form-input"
            required
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="form-label block">
          Descrição
          <textarea
            className="form-input"
            required
            maxLength={10000}
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="form-label block">
          Prioridade
          <select
            className="form-input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as ProtocolPriority)}
          >
            {Object.entries(priorityLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {params.get("conversationId") ? (
          <p>Solicitante obtido do atendimento de origem.</p>
        ) : (
          <PartySelect
            kind="user"
            label="Solicitante (opcional)"
            value={requesterId}
            onChange={setRequesterId}
          />
        )}
        <PartySelect
          kind="system-user"
          label="Responsável (opcional)"
          value={assignedToSystemUserId}
          onChange={setAssigned}
        />
        <ProtocolAttachmentsSection
          selectedFiles={files}
          onSelectedFilesChange={setFiles}
        />
        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={busy}
          loadingLabel="Registrando"
          disabled={busy || !title.trim() || !description.trim()}
        >
          Registrar protocolo
        </LoadingButton>
      </form>
    </div>
  );
}
